import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CButton,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CSpinner,
  CFormInput,
  CInputGroup,
  CInputGroupText,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilTrash,
  cilPlus,
  cilPencil,
  cilSearch,
  cilWarning,
  cilX,
} from "@coreui/icons";
import AddCustomer from "./AddCustomer";
import styles from "./Customers.module.scss";

const Customers = ({ visible, onClose }) => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [positions, setPositions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Функция поиска
  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredCustomers(customers);
      return;
    }
    const lowerTerm = term.toLowerCase();
    const filtered = customers.filter((customer) => {
      return (
        (customer.Name && customer.Name.toLowerCase().includes(lowerTerm)) ||
        (customer.ShortName &&
          customer.ShortName.toLowerCase().includes(lowerTerm)) ||
        (customer.Director &&
          customer.Director.toLowerCase().includes(lowerTerm)) ||
        (customer.RegNum && customer.RegNum.toString().includes(term)) ||
        (customer.Address &&
          customer.Address.toLowerCase().includes(lowerTerm)) ||
        (customer.Phone && customer.Phone.includes(term))
      );
    });
    setFilteredCustomers(filtered);
  };

  const resetSearch = () => {
    setSearchTerm("");
    setFilteredCustomers(customers);
  };

  const handleAddClick = () => {
    setSelectedCustomer(null);
    setAddModalVisible(true);
    resetSearch();
  };

  const handleEditClick = () => {
    if (selectedCustomer) {
      setAddModalVisible(true);
    } else {
      alert("Пожалуйста, выберите заказчика для редактирования");
    }
  };

  const handleDeleteClick = () => {
    if (selectedCustomer) {
      setDeleteConfirmVisible(true);
    } else {
      alert("Пожалуйста, выберите заказчика для удаления");
    }
  };

  // Главная функция загрузки данных
  const fetchCustomers = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    try {
      // Запускаем запросы параллельно: заказчики и справочник должностей
      const [customersRes, positionsRes] = await Promise.all([
        axios.get("/api/admin/customers/", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("/api/typetable/?ntype=62", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      // 1. Обработка должностей (создаем карту Id -> Name)
      const positionsMap = {};
      if (Array.isArray(positionsRes.data)) {
        positionsRes.data.forEach((pos) => {
          positionsMap[pos.Id] = pos.Name;
        });
      }
      setPositions(positionsMap);

      // 2. Обработка заказчиков (ваша логика уникальности)
      const rawData = customersRes.data;
      const getFilledCount = (item) =>
        Object.values(item).filter(
          (v) => v !== null && v !== "" && v !== undefined,
        ).length;

      const uniqueMap = rawData.reduce((acc, current) => {
        const key =
          current.RegNum || current.RegNum === 0
            ? current.RegNum
            : `empty-${current.Id}`;
        if (!acc[key] || getFilledCount(current) > getFilledCount(acc[key])) {
          acc[key] = current;
        }
        return acc;
      }, {});

      const sortedData = Object.values(uniqueMap).sort((a, b) => {
        const numA = a.RegNum || a.RegNum === 0 ? Number(a.RegNum) : Infinity;
        const numB = b.RegNum || b.RegNum === 0 ? Number(b.RegNum) : Infinity;
        if (numA !== numB) return numA - numB;
        return a.Id - b.Id;
      });

      setCustomers(sortedData);
      setFilteredCustomers(sortedData);
    } catch (error) {
      console.error("Ошибка при загрузке данных:", error);
      alert("Ошибка при загрузке данных с сервера.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCustomer) return;
    const token = localStorage.getItem("token");
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/admin/customers/${selectedCustomer.Id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteConfirmVisible(false);
      setSelectedCustomer(null);
      await fetchCustomers();
      alert("Заказчик успешно удален");
    } catch (error) {
      console.error("Ошибка при удалении заказчика:", error);

      // Проверяем разные варианты ошибок
      if (error.response) {
        if (error.response.status === 404) {
          alert("Заказчик не найден. Возможно, он уже был удален.");
        } else if (error.response.status === 403) {
          alert("У вас нет прав для удаления заказчиков");
        } else if (error.response.status === 409) {
          alert(
            "Невозможно удалить заказчика. Возможно, есть связанные записи.",
          );
        } else {
          alert(`Ошибка сервера: ${error.response.status}`);
        }
      } else if (error.request) {
        alert(
          "Не удалось соединиться с сервером. Проверьте подключение к интернету.",
        );
      } else {
        alert("Произошла ошибка при удалении");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const modalStyle = {
    // height: "40vh",
    // maxWight: "70vw",
    display: "flex",
    flexDirection: "column",
  };

  useEffect(() => {
    if (visible) {
      fetchCustomers();
      resetSearch();
    }
  }, [visible]);

  useEffect(() => {
    setFilteredCustomers(customers);
  }, [customers]);

  return (
    <>
      <CModal
        visible={visible}
        onClose={onClose}
        size="xl"
        className={styles.customers__modal}
        scrollable
      >
        <CModalHeader>
          <CModalTitle>Справочник: Заказчики</CModalTitle>
        </CModalHeader>

        <div className="mt-3 mb-3 px-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex gap-2">
              <CButton className="button" onClick={handleAddClick}>
                <CIcon
                  icon={cilPlus}
                  className="customers__svg customers__svg--plus me-1"
                />
                Добавить
              </CButton>
              <CButton
                className="button"
                onClick={handleEditClick}
                disabled={!selectedCustomer}
              >
                <CIcon icon={cilPencil} className="customers__svg me-1" />
                Изменить
              </CButton>
              <CButton
                className="button"
                onClick={handleDeleteClick}
                disabled={!selectedCustomer}
              >
                <CIcon
                  icon={cilTrash}
                  className="customers__svg customers__svg--delete me-1"
                />
                Удалить
              </CButton>
            </div>

            <div className="d-flex align-items-center gap-2 ms-3">
              <CInputGroup style={{ width: "500px" }}>
                <CInputGroupText>
                  <CIcon icon={cilSearch} />
                </CInputGroupText>
                <CFormInput
                  placeholder="Поиск"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                {searchTerm && (
                  <CButton
                    color="secondary"
                    variant="outline"
                    onClick={resetSearch}
                  >
                    <CIcon icon={cilX} />
                  </CButton>
                )}
              </CInputGroup>
              {searchTerm && (
                <span className="text-muted small">
                  Найдено: {filteredCustomers.length}
                </span>
              )}
            </div>
          </div>
        </div>

        <CModalBody>
          <div className={styles.table_wrapper}>
            {loading ? (
              <div className="text-center p-5">
                <CSpinner color="primary" />
                <h5 className="text-muted mt-2">Загрузка данных...</h5>
              </div>
            ) : (
              <CTable className="customers__table" bordered hover responsive>
                <CTableHead className="customers__head">
                  <CTableRow>
                    <CTableHeaderCell>
                      Регистрационный <br />
                      номер
                    </CTableHeaderCell>
                    <CTableHeaderCell>Наименование</CTableHeaderCell>
                    <CTableHeaderCell>
                      Сокращенное <br /> наименование
                    </CTableHeaderCell>
                    <CTableHeaderCell>Почтовый адрес</CTableHeaderCell>
                    <CTableHeaderCell>Руководитель</CTableHeaderCell>
                    <CTableHeaderCell>Должность</CTableHeaderCell>
                    <CTableHeaderCell>Телефон</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody className="customers__body">
                  {filteredCustomers.map((item) => {
                    const postId = item.idPost || item.IdPost || item.idpost;
                    return (
                      <CTableRow
                        key={item.Id}
                        active={selectedCustomer?.Id === item.Id}
                        onClick={() => setSelectedCustomer(item)}
                        style={{ cursor: "pointer" }}
                      >
                        <CTableDataCell>{item.RegNum}</CTableDataCell>
                        <CTableDataCell>{item.Name}</CTableDataCell>
                        <CTableDataCell>{item.ShortName}</CTableDataCell>
                        <CTableDataCell>{item.Address}</CTableDataCell>
                        <CTableDataCell>{item.Director}</CTableDataCell>
                        <CTableDataCell>
                          {positions[postId] || "—"}
                        </CTableDataCell>
                        <CTableDataCell>{item.Phone}</CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
              </CTable>
            )}
          </div>
        </CModalBody>
      </CModal>

      {/* Модалка подтверждения удаления */}
      <CModal
        visible={deleteConfirmVisible}
        size="md"
        style={modalStyle}
        onClose={() => !deleteLoading && setDeleteConfirmVisible(false)}
      >
        <CModalHeader>
          <CModalTitle className="d-flex align-items-center">
            <CIcon icon={cilWarning} className="text-danger me-2" />
            Подтверждение удаления
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>Вы уверены, что хотите удалить заказчика?</p>
          <div className="alert alert-warning">
            <strong>
              {selectedCustomer?.Name || selectedCustomer?.ShortName}
            </strong>
            {selectedCustomer?.RegNum && (
              <div>Рег. номер: {selectedCustomer.RegNum}</div>
            )}
          </div>
          <p className="text-danger small">
            <CIcon icon={cilWarning} /> Это действие нельзя отменить.
          </p>
          <div className="d-flex justify-content-end gap-2">
            <CButton
              color="secondary"
              onClick={() => setDeleteConfirmVisible(false)}
            >
              Отмена
            </CButton>
            <CButton
              color="danger"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              Удалить
            </CButton>
          </div>
        </CModalBody>
      </CModal>

      {/* Модалка добавления/редактирования */}
      <AddCustomer
        visible={addModalVisible}
        onClose={() => setAddModalVisible(false)}
        onCustomerAdded={() => {
          fetchCustomers();
          setSelectedCustomer(null);
          resetSearch();
        }}
        customerData={selectedCustomer}
      />
    </>
  );
};

export default Customers;
